import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import { fetchPointsHistory } from "./actions/fetchPointsHistory";
import { fetchPointsHistoryReferral } from "./actions/fetchReferralHistory";
import { fetchUserLoyaltyProfile } from "./actions/fetchUserLoyaltyProfile";
import { fetchUserPoints } from "./actions/fetchUserPoints";
import { handleUser } from "./actions/handleUser";

import {
  type HistoryPointsType,
  type HistoryReferral,
  type LoyaltyCoreReducerType,
  type LoyaltyReminderState,
  type Points,
  type ReferreeStatsType,
  Status,
  type TierData,
} from "./loyaltyCoreSlice.types";

export const initialHistoryPoints: HistoryPointsType = {
  isLoaded: false,
  items: [],
  total: 0,
  statusCode: 0,
};

export const initialHistoryReferral: HistoryReferral = {
  isLoaded: false,
  items: [],
  total: 0,
};

export const initialReferralStats: ReferreeStatsType = {
  earned_points: 0,
  referees_count: 0,
  completed_first_order_count: 0,
};

export const initialTier: TierData = {
  name: "",
  image_url: "",
  next_tier_name: "",
  points_to_next_tier: 0,
  current_tier_assignment_date: "",
  cumulative_points: 0,
  points_to_maintain_current_tier: 0,
  current_tier_secured_till: "",
};

export const pointsInitial: Points = { balance: 0, status: Status.IDLE, expire_at: "", points_to_expire: 0 };

export const initialLoyaltyReminderState: LoyaltyReminderState = {
  hasFirstPurchase: false,
  parentCodeExists: false,
};

export const initialStateLoyaltyCore: LoyaltyCoreReducerType = {
  name: "",
  surname: "",
  uuid: "",
  points: pointsInitial,
  rewards: 0,
  ciamToken: "",
  code: "",
  tier: initialTier,
  referrees: [
    {
      createdAccount: true,
      completedFirstOrder: false,
    },
  ],
  referralStats: initialReferralStats,
  historyPoints: initialHistoryPoints,
  historyReferral: initialHistoryReferral,
  isLoaded: false,
  isLogged: false,
  isRestricted: false,
  isReferralOn: false,
  isUserDataFetching: false,
  reminder: initialLoyaltyReminderState,
};

export const loyaltyCoreSlice = createSlice({
  name: "loyaltyCore",
  initialState: initialStateLoyaltyCore,
  reducers: {
    setLoggedState: (state, { payload }: PayloadAction<boolean>) => {
      state.isLogged = payload;
    },
    setLoadedState: (state, { payload }: PayloadAction<boolean>) => {
      state.isLoaded = payload;
    },
    setRestrictedState: (state, { payload }: PayloadAction<boolean>) => {
      state.isRestricted = payload;
    },
    setUser: (state, { payload }: PayloadAction<Partial<LoyaltyCoreReducerType>>) => {
      state.name = payload.name || state.name;
      state.surname = payload.surname || state.surname;
      state.ciamToken = payload.ciamToken || state.ciamToken;
      state.code = payload.code || state.code;
      state.referralStats = payload.referralStats || state.referralStats;
      state.tier = payload.tier || state.tier;
      state.uuid = payload.uuid || state.uuid;
    },
    setReferralStatus: (state, { payload }: PayloadAction<Partial<boolean>>) => {
      state.isReferralOn = payload;
    },
    setLoyaltyReminder: (state, action: PayloadAction<Partial<LoyaltyReminderState>>) => {
      if (action.payload.hasFirstPurchase !== undefined) {
        state.reminder.hasFirstPurchase = action.payload.hasFirstPurchase;
      }
      if (action.payload.parentCodeExists !== undefined) {
        state.reminder.parentCodeExists = action.payload.parentCodeExists;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPointsHistory.fulfilled, (state, action) => {
      const { payload, meta } = action;
      const { pageNo } = meta.arg;

      // DESP-106963 - Prevent duplicated items on EVA dashboard:
      // Navigating between `/club/account` and `/club/account/points-history` with Next.js links (no full page reload)
      // remounts PointsHistory and triggers another fetch for page 1, causing duplicate data.
      // Fix: replace list when pageNo === 1.
      const shouldResetItems = pageNo === 1;
      const payloadItems = [...(payload.items ?? [])];
      const currentHistoryPoints = state.historyPoints ?? initialHistoryPoints;

      state.historyPoints = {
        isLoaded: payload.isLoaded,
        items: shouldResetItems ? payloadItems : [...currentHistoryPoints.items, ...payloadItems],
        total: payload.total ?? currentHistoryPoints.total,
        statusCode: payload.statusCode,
      };
    });
    builder.addCase(fetchPointsHistory.rejected, (state, { payload }) => {
      const currentHistoryPoints = state.historyPoints ?? initialHistoryPoints;
      state.historyPoints = {
        ...currentHistoryPoints,
        isLoaded: true,
        message: payload?.message,
        statusCode: payload?.code ?? 0,
      };
    });
    builder.addCase(fetchPointsHistoryReferral.fulfilled, (state, { payload }) => {
      const current = state.historyReferral ?? initialHistoryReferral;
      if (!payload) {
        state.historyReferral = { ...current, isLoaded: true };
        return;
      }
      state.historyReferral = {
        items: payload.items ? [...payload.items] : [...current.items],
        total: payload.total ?? current.total,
        isLoaded: true,
      };
    });
    builder.addCase(fetchPointsHistoryReferral.rejected, (state) => {
      const current = state.historyReferral ?? initialHistoryReferral;
      state.historyReferral = { ...current, isLoaded: true };
    });
    builder.addCase(fetchUserPoints.pending, (state) => {
      state.points.status = Status.PENDING;
    });
    builder.addCase(fetchUserPoints.fulfilled, (state, { payload }) => {
      state.points = payload;
    });
    builder.addCase(fetchUserPoints.rejected, (state) => {
      state.points.status = Status.FAILED;
    });
    builder.addCase(fetchUserLoyaltyProfile.pending, (state) => {
      state.points.status = Status.PENDING;
    });
    builder.addCase(fetchUserLoyaltyProfile.fulfilled, (state, { payload }) => {
      state.points = payload.points;
      state.tier = payload.tier;
    });
    builder.addCase(fetchUserLoyaltyProfile.rejected, (state) => {
      state.points.status = Status.FAILED;
    });
    builder.addCase(handleUser.pending, (state) => {
      state.isUserDataFetching = true;
    });
    builder.addCase(handleUser.fulfilled, (state) => {
      state.isUserDataFetching = false;
    });
    builder.addCase(handleUser.rejected, (state) => {
      state.isUserDataFetching = false;
    });
  },
});

export const { setLoggedState, setLoadedState, setRestrictedState, setUser, setReferralStatus, setLoyaltyReminder } =
  loyaltyCoreSlice.actions;

export default loyaltyCoreSlice.reducer;
